<template>
  <div class="buttons are-small">
    <button
      :class="option == 'last 6 months' ? 'is-link is-active' : 'is-light'"
      class="button"
      @click="option = 'last 6 months'"
    >
      Last 6 Months
    </button>
    <button
      :class="option == 'this year' ? 'is-link is-active' : 'is-light'"
      class="button"
      @click="option = 'this year'"
    >
      This Year
    </button>
    <button
      :class="option == 'all time' ? 'is-link is-active' : 'is-light'"
      class="button"
      @click="option = 'all time'"
    >
      All Time
    </button>
  </div>
  <div ref="chart" class="chart" />
</template>

<script>
import { find, groupBy, maxBy, minBy } from 'lodash-es';
import vegaEmbed from 'vega-embed';
import { dateToSerialNumber, serialNumberToDate } from '@/utils';

export default {
  numberFormat: new Intl.NumberFormat('en-US', {
    style: 'decimal',
    maximumFractionDigits: 2,
  }),

  props: {
    items: {
      type: Array,
      required: true,
    },

    totalIdx: {
      type: Number,
      required: true,
    },

    color: {
      type: String,
      default: '#1f77b4',
    },
  },

  data() {
    return {
      option: 'last 6 months',
    };
  },

  computed: {
    dateRange() {
      const now = new Date();

      switch (this.option) {
        case 'last 6 months':
          return [
            dateToSerialNumber(
              new Date(now.getFullYear(), now.getMonth() - 5, 1)
            ),
            dateToSerialNumber(
              new Date(now.getFullYear(), now.getMonth() + 1, 0)
            ),
          ];
        case 'this year':
          return [
            dateToSerialNumber(new Date(now.getFullYear(), 0, 1)),
            dateToSerialNumber(new Date(now.getFullYear() + 1, 0, 0)),
          ];
        default:
          return null;
      }
    },

    itemsFiltered() {
      return this.dateRange
        ? this.items.filter(
            i => i[0] >= this.dateRange[0] && i[0] <= this.dateRange[1]
          )
        : this.items;
    },

    values() {
      const groups = groupBy(this.itemsFiltered, i => {
        const date = serialNumberToDate(i[0]);

        return [
          date.getFullYear(),
          String(date.getMonth() + 1).padStart(2, 0),
        ].join('-');
      });

      const items = Object.entries(groups).map(([yearMonth, items]) => ({
        yearMonth,
        total: items.reduce((total, i) => total + i[this.totalIdx], 0),
      }));

      const min = minBy(items, i => new Date(i.yearMonth));
      const max = maxBy(items, i => new Date(i.yearMonth));

      const startDate = new Date(min.yearMonth);
      const endDate = new Date(max.yearMonth);

      let data = [];

      for (
        let year = startDate.getFullYear();
        year <= endDate.getFullYear();
        year++
      ) {
        for (
          let month = startDate.getMonth() + 1;
          month <=
          (year == endDate.getFullYear() ? endDate.getMonth() + 1 : 12);
          month++
        ) {
          const yearMonth = `${year}-${String(month).padStart(2, 0)}`;
          const match = find(items, { yearMonth });

          data.push({
            yearMonth,
            total: match ? match.total : 0,
            color: this.color,
          });
        }
      }

      return data;
    },
  },

  watch: {
    values() {
      this.vegaView.data('summary', this.values).run();
    },
  },

  async mounted() {
    const { view } = await this.plot();

    this.vegaView = view;
  },

  methods: {
    plot() {
      const spec = {
        $schema: 'https://vega.github.io/schema/vega-lite/v4.json',
        width: 'container',
        height: 250,
        data: {
          name: 'summary',
          values: this.values,
        },
        mark: 'bar',
        encoding: {
          x: {
            field: 'yearMonth',
            type: 'ordinal',
            axis: {
              title: null,
              labelAngle: 35,
            },
          },
          y: {
            field: 'total',
            type: 'quantitative',
          },
          color: {
            field: 'color',
            scale: null,
          },
          tooltip: {
            field: 'total',
            type: 'quantitative',
          },
        },
      };

      return vegaEmbed(this.$refs.chart, spec);
    },
  },
};
</script>

<style scoped>
.chart {
  width: 100%;
  height: 250px;
}
</style>
